import AlertCircle from "@uui/AlertCircle";
import CheckCircle from "@uui/CheckCircle";
import ChevronDown from "@uui/ChevronDown";

import {
  Accordion,
  AccordionProps,
  AccordionDetails,
  AccordionDetailsProps,
  AccordionSummary,
  AccordionSummaryProps,
  accordionClasses,
  Button,
  Divider,
  Typography,
  SvgIconProps,
  ButtonProps,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import { SemanticScore } from "@app/common/utils";

import IconBackground from "@rui/IconBackground";
import { Row, StackProps } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";

const ExpandableCoachingModule = styled((props: AccordionProps) => (
  <Accordion disableGutters elevation={0} {...props} />
))(({ theme }) => ({
  padding: "12px 16px",
  transition: "border 200ms ease-out",
  borderRadius: theme.corners.base,
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  [`&.${accordionClasses.expanded}`]: {
    borderColor: theme.palette.shades.primary[500],
  },
  "&::before": { display: "none" },
  marginBottom: theme.spacing(1),
}));

type UnexpandedCoachingModuleProps = StackProps & { selected?: boolean };

const UnexpandedCoachingModuleProps = styled(
  ({ selected: _, ...props }: UnexpandedCoachingModuleProps) => (
    <Row {...props} variant="apart" />
  )
)(({ selected, theme }) => ({
  marginTop: theme.spacing(1),
  padding: "12px 16px",
  borderRadius: theme.corners.base,
  border: "2px solid",
  borderColor: selected
    ? theme.palette.shades.primary[500]
    : theme.palette.shades.neutral[100],
  "&:hover": {
    cursor: "pointer",
    borderColor: selected
      ? theme.palette.shades.primary[500]
      : theme.palette.shades.ghost[700],
  },
}));

type CoachingModuleProps = (UnexpandedCoachingModuleProps | AccordionProps) & {
  expandable?: boolean;
};

export const CoachingModule = ({
  expandable = false,
  ...props
}: CoachingModuleProps) => {
  if (expandable) {
    return <ExpandableCoachingModule {...(props as AccordionProps)} />;
  }

  return (
    <UnexpandedCoachingModuleProps
      {...(props as UnexpandedCoachingModuleProps)}
    />
  );
};

export const CoachingModuleHeader = styled(
  ({ children, ...props }: AccordionSummaryProps) => (
    <AccordionSummary
      expandIcon={
        <ChevronDown sx={{ fontSize: "1rem", color: "shades.neutral.400" }} />
      }
      {...props}
    >
      <Row variant="apart" flexWrap="wrap" sx={{ width: "100%", pr: 2 }}>
        {children}
      </Row>
    </AccordionSummary>
  )
)({
  padding: 0,
  minHeight: 0,
  "& .MuiAccordionSummary-content": {
    margin: 0,
  },
});

interface CoachingModuleTitleProps {
  children: React.ReactNode;
  Icon: React.JSXElementConstructor<SvgIconProps>;
  tooltip?: string;
}

export const CoachingModuleTitle = styled(
  ({ children, Icon, tooltip, ...props }: CoachingModuleTitleProps) => (
    <Row variant="left" spacing={1} {...props}>
      <IconBackground height={24} sx={{ bgcolor: "shades.ghost.500" }}>
        <Icon sx={{ color: "shades.primary.500" }} />
      </IconBackground>
      <Typography variant="bodyPrimaryMed" sx={{ whiteSpace: "nowrap" }}>
        {children}
      </Typography>
      {tooltip && <TooltipIcon title={tooltip} />}
    </Row>
  )
)(({ theme }) => ({
  color: theme.palette.shades.neutral[700],
}));

export interface CoachingModuleSummaryProps extends ButtonProps {
  icon: SemanticScore;
}

export const CoachingModuleSummary = styled(
  ({ children, icon, ...props }: CoachingModuleSummaryProps) => (
    <Button
      {...props}
      endIcon={
        icon === "positive" ? (
          <CheckCircle />
        ) : icon === "unknown" ? undefined : (
          <AlertCircle />
        )
      }
    >
      <Typography noWrap>{children}</Typography>
    </Button>
  )
)(({ theme, icon }) => ({
  minHeight: 0,
  minWidth: 0,
  width: "fit-content",
  backgroundColor: "#FFF",
  height: "auto",
  padding: "0 8px 0 4px",
  ...(icon === "unknown" && { paddingLeft: "8px" }),
  color: theme.palette.shades.neutral[800],
  "&:hover": {
    color: theme.palette.shades.primary[500],
  },
  "& .MuiButton-endIcon svg": {
    color:
      icon === "positive"
        ? theme.palette.shades.meadow[500]
        : theme.palette.shades.orange[500],
    fontSize: theme.sizes.icon.small,
  },
}));

export const CoachingModuleEmptySummary = styled(Typography)(({ theme }) => ({
  color: theme.palette.shades.neutral[500],
}));

export const CoachingModuleContent = styled(
  ({ children, ...props }: AccordionDetailsProps) => (
    <AccordionDetails {...props}>
      <Divider sx={{ my: 1.5 }} />
      {children}
    </AccordionDetails>
  )
)({ padding: 0 });
